import dataCollectorHandlers from '../../consts/dataCollector/dataCollectorHandlers.ts';
import dataCollectorPollingTypes from '../../consts/dataCollector/dataCollectorPollingTypes.ts';
import * as dataCollectorSettingsTypes from '../../consts/dataCollector/dataCollectorSettingsTypes';
import dataCollectorWorkModes from '../../consts/dataCollector/dataCollectorWorkModes.ts';
import {makeSelectCurrentDataCollector} from '../../state/selectors/dataCollectors';
import {getState} from '../../state/store';
import stringUtils from '../../utils/stringUtils';

export const currentDataCollectorMapping = (data) => {
    const settings = {...data?.smartSettings, ...data.protocolSettings};

    return {...data, ...settings};
};

export const dataCollectorListMapping = (data) => {
    const filters = data?.paginatedData?.filters || data?.filters || {};
    const modelIDs = filters.ModelID || {};

    const dataCollectorTypes = Object.keys(modelIDs).map((key) => ({
        key,
        value: modelIDs[key],
    }));

    const mapItem = (item) => ({
        key: item.id,
        id: item.id,
        ...item,
    });

    if (data && data.paginatedData) {
        return {
            paginatedData: {
                ...data.paginatedData,
                items: data.paginatedData.items ? data.paginatedData.items.map(mapItem) : [],
            },
            quarantineItems: data.quarantineItems ? data.quarantineItems.map(mapItem) : [],
            dataCollectorTypes,
            filters,
            sortableColumns: data.paginatedData.sortableColumns || [],
        };
    }

    return {
        paginatedData: {
            items: data.items ? data.items.map(mapItem) : [],
            page: data.page,
            size: data.size,
            totalFilteredRecords: data.totalFilteredRecords,
        },
        quarantineItems: [],
        dataCollectorTypes,
        filters,
        sortableColumns: data.sortableColumns || [],
    };
};

const buildDataCollectorRequest = (values) => {
    const {
        server,
        folder,
        login,
        password,
        source,
        format,
        uploadIntervalHour,
        pollingIntervalMinutes,
        pollingType = dataCollectorPollingTypes.Interval,
        pollingSchedule,
        tcpPort,
        useDefaultUploadInterval,
        gatewayEui,
        workMode,
    } = values || {};

    const isLoraWan = +workMode === dataCollectorWorkModes.Gateway;
    const isServer = +workMode === dataCollectorWorkModes.Server;

    const rawHandler = values?.handler?.value !== undefined ? values.handler.value : values?.handler;
    const selectedHandlerValue = rawHandler != null ? Number(rawHandler) : undefined;

    const actualHandlerValue = isLoraWan
        ? dataCollectorHandlers.Json
        : isServer
        ? selectedHandlerValue || dataCollectorHandlers.Smart
        : selectedHandlerValue;

    const actualSourceValue = isServer ? null : isLoraWan ? 3 : source != null ? Number(source) : source;

    const isSmart =
        actualHandlerValue === dataCollectorHandlers.Smart ||
        actualHandlerValue === dataCollectorHandlers.Json ||
        actualHandlerValue === dataCollectorHandlers.MBus ||
        actualHandlerValue === dataCollectorHandlers.ModBus ||
        actualHandlerValue === dataCollectorHandlers.Svtu11Rp;

    const isJson = actualHandlerValue === dataCollectorHandlers.Json;

    const settingsType = isSmart
        ? dataCollectorSettingsTypes.SMART_SETTINGS
        : dataCollectorSettingsTypes.PROTOCOL_SETTINGS;

    const isHttp = +actualSourceValue === 3;

    const mappedValues = {
        name: values.name,
        deviceNumber: values.deviceNumber,
        ipAddress: values.ipAddress,
        simNumber: values.simNumber,
        workMode: values.workMode,
        gatewayEui: gatewayEui?.key !== undefined ? gatewayEui.key : gatewayEui,

        isDataTransferAllowed: stringUtils.getBooleanFromString(values.isDataTransferAllowed),

        handler: actualHandlerValue,
        status: values.status,
        pollingIntervalMinutes: pollingIntervalMinutes ? parseInt(pollingIntervalMinutes, 10) : null,
        pollingType: Number(pollingType),
        pollingSchedule: Array.isArray(pollingSchedule)
            ? pollingSchedule
                  .filter(
                      ({dayOfWeek, hour, minute}) =>
                          Number.isInteger(dayOfWeek) && Number.isInteger(hour) && Number.isInteger(minute)
                  )
                  .map(({dayOfWeek, hour, minute}) => ({dayOfWeek, hour, minute}))
            : [],
        uspdTypeId: values.typeId?.key !== undefined ? values.typeId.key : values.typeId,
        buildingId: values.buildingId?.key !== undefined ? values.buildingId.key : values.buildingId,

        smartSettings: null,
        protocolSettings: null,
    };

    mappedValues[settingsType] = isServer
        ? {
              server: values.ipAddress,
              port: parseInt(tcpPort, 10),
          }
        : {
              server: isHttp ? server || '-' : server,
              folder: isHttp ? folder || '' : folder,
              login: isHttp ? login || '-' : login,
              password: isHttp ? password || '-' : password,
              format: 1,
          };

    if (!isServer && (isLoraWan || (useDefaultUploadInterval === 'false' && uploadIntervalHour))) {
        mappedValues.uploadIntervalHour = parseInt(uploadIntervalHour, 10);
    } else {
        mappedValues.uploadIntervalHour = null;
    }

    if (actualSourceValue != null) {
        mappedValues[settingsType].source = actualSourceValue;
    }

    if (!isServer && format && !isJson) {
        mappedValues[settingsType].format = format?.key !== undefined ? format.key : format;
    }

    return mappedValues;
};

export const createDataCollectorRequestMapping = (values) => {
    return buildDataCollectorRequest(values);
};

export const editDataCollectorRequestMapping = (values) => {
    const currentDataCollector = makeSelectCurrentDataCollector()(getState());

    const mappedValues = buildDataCollectorRequest(values);

    if (currentDataCollector?.uspdID) {
        mappedValues.uspdID = currentDataCollector.uspdID;
    }

    return mappedValues;
};
